/**
 * Clarity Attribution Capture (v3 — intent-aware stamping)
 *
 * Why v3 exists:
 *   v2 only stamped on /cart/add or on /cart, /checkout pages.
 *   Real customer behaviour on Clarity Inc bypasses BOTH paths constantly:
 *     - PayPal/Shop Pay/Apple Pay/Google Pay buy-now buttons on the product page
 *       create the cart server-side and redirect straight to an external checkout
 *       → no /cart/add fetch and no /cart/* page is ever visited.
 *   Result: ~88% of orders had empty note_attributes (audited 2026-06-02 → 2026-06-16),
 *   even when the landing URL had fbclid/gclid/utm. This is the attribution leak.
 *
 * Captures (unchanged from v2):
 *   - URL params (gclid, fbclid, utm_source/medium/campaign/content/term)
 *   - Session data (source, medium, referrer, landing page)
 *   - First touch (90-day localStorage) + Last touch (sessionStorage)
 *
 * v3 stamping triggers (any of these — first one wins, idempotent for session):
 *   1. /cart/add fetch + XHR success           (regular add-to-cart flow)
 *   2. /cart, /cart/*, /checkout/* page load   (regular checkout flow)
 *   3. ANY click on a payment / checkout / buy-now button   (express checkout)
 *      Delegated click listener on common selectors, in CAPTURE phase so we
 *      stamp BEFORE the redirect fires.
 *      Uses fetch with keepalive:true so the request survives navigation.
 *   4. Shopify payment_methods shown event     (Shop Pay drawer / wallet UI)
 *
 * No-phantom-cart guard:
 *   We never stamp on bare page load. We only stamp when there is purchase intent
 *   (add-to-cart success, payment-button click, or visiting /cart|/checkout).
 *   Window-shoppers still create zero phantom carts.
 *
 * Deploy: included via {{ 'cart-stamp.js' | asset_url | script_tag }} in theme.liquid
 */

(function () {
  'use strict';

  // --- CONFIG ---
  const FIRST_TOUCH_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
  const STORAGE_PREFIX = 'clarity_attr_';
  const STAMPED_FLAG_KEY = 'clarity_attr_stamped_this_session';
  const VERSION = 'v3';

  const CAPTURE_PARAMS = [
    'gclid', 'fbclid',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'
  ];

  // Selectors that indicate "this click is about to initiate checkout".
  // Cast a wide net — false positives are cheap (we just stamp the cart once),
  // false negatives cost us attribution data.
  const PAYMENT_SELECTORS = [
    // Shopify standard checkout buttons
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href="/checkout"]',
    'a[href^="/checkout?"]',
    'a[href^="/checkout/"]',
    // Dynamic checkout / wallet buttons rendered by Shopify
    '[data-shopify="payment-button"]',
    '.shopify-payment-button',
    '.shopify-payment-button__button',
    '.shopify-payment-button button',
    'shop-pay-wallet-button',
    'shop-pay-button',
    // Form submits to /cart/add (catches Buy-now-as-add flows)
    'form[action="/cart/add"] [type="submit"]',
    'form[action^="/cart/add"] [type="submit"]'
  ];

  // --- HELPERS ---
  function getUrlParams() {
    const params = {};
    const sp = new URLSearchParams(window.location.search);
    CAPTURE_PARAMS.forEach((k) => { if (sp.has(k)) params[k] = sp.get(k); });
    return params;
  }

  function deriveSource(ref) {
    if (!ref) return 'direct';
    try {
      const host = new URL(ref).hostname;
      if (host.includes('google')) return 'google';
      if (host.includes('facebook') || host.includes('fb.')) return 'facebook';
      if (host.includes('instagram')) return 'instagram';
      if (host === window.location.hostname) return '(internal)';
      return host;
    } catch (e) { return 'unknown'; }
  }

  function deriveMedium(ref) {
    if (!ref) return 'none';
    try {
      const host = new URL(ref).hostname;
      if (host.includes('google')) return 'organic';
      if (host.includes('facebook') || host.includes('instagram') || host.includes('fb.')) return 'social';
      if (host === window.location.hostname) return '(internal)';
      return 'referral';
    } catch (e) { return 'unknown'; }
  }

  function getSessionData() {
    return {
      landing_page: window.location.href,
      referrer: document.referrer || '(direct)',
      session_source: deriveSource(document.referrer),
      session_medium: deriveMedium(document.referrer)
    };
  }

  function safeLS(key, value) {
    try {
      if (value !== undefined) {
        localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
      } else {
        const v = localStorage.getItem(STORAGE_PREFIX + key);
        return v ? JSON.parse(v) : null;
      }
    } catch (e) { return null; }
  }

  function safeSS(key, value) {
    try {
      if (value !== undefined) {
        sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
      } else {
        const v = sessionStorage.getItem(STORAGE_PREFIX + key);
        return v ? JSON.parse(v) : null;
      }
    } catch (e) { return null; }
  }

  function getFirstTouch() {
    const s = safeLS('first_touch');
    if (s && s.timestamp && (Date.now() - s.timestamp < FIRST_TOUCH_TTL_MS)) return s.data;
    return null;
  }
  function setFirstTouch(d) { safeLS('first_touch', { data: d, timestamp: Date.now() }); }
  function setLastTouch(d) { safeSS('last_touch', d); }

  function buildAttributes() {
    const firstTouch = getFirstTouch() || {};
    const lastTouch = safeSS('last_touch') || {};
    const attrs = {};
    Object.keys(firstTouch).forEach((k) => { attrs['ft_' + k] = firstTouch[k]; });
    Object.keys(lastTouch).forEach((k) => { attrs['lt_' + k] = lastTouch[k]; });
    attrs.attribution_captured_at = new Date().toISOString();
    attrs.attribution_version = VERSION;
    return attrs;
  }

  // --- CAPTURE (free — no API call) ---
  function captureAttribution() {
    const urlParams = getUrlParams();
    const sessionData = getSessionData();
    const current = Object.assign({}, urlParams, sessionData);
    if (!getFirstTouch()) setFirstTouch(current);
    setLastTouch(current);
  }

  // --- STAMP ---
  // useKeepalive=true is critical for express-checkout clicks: the page is
  // about to navigate, so the request must survive unload. fetch(keepalive)
  // is supported in all evergreen browsers; we sendBeacon-fallback isn't needed
  // because Shopify rejects non-JSON for /cart/update.js.
  function stampCart(useKeepalive) {
    if (sessionStorage.getItem(STAMPED_FLAG_KEY) === '1') return;

    const attrs = buildAttributes();
    // Bail if we have no captured data (only timestamp + version)
    if (Object.keys(attrs).length <= 2) return;

    const body = JSON.stringify({ attributes: attrs });
    const opts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body
    };
    if (useKeepalive) opts.keepalive = true;

    // Mark as stamped BEFORE the request fires. If the page navigates away
    // before fetch resolves, we still don't want a second stamp attempt
    // on the next page in this session.
    sessionStorage.setItem(STAMPED_FLAG_KEY, '1');

    try {
      fetch('/cart/update.js', opts).catch(() => {
        // On hard failure, allow a retry next time
        sessionStorage.removeItem(STAMPED_FLAG_KEY);
      });
    } catch (e) {
      sessionStorage.removeItem(STAMPED_FLAG_KEY);
    }
  }

  // --- INTERCEPTORS: /cart/add via fetch + XHR ---
  function installAddToCartHook() {
    if (window.fetch) {
      const origFetch = window.fetch;
      window.fetch = function (input, init) {
        const url = (typeof input === 'string') ? input : (input && input.url) || '';
        const isAdd = url.indexOf('/cart/add') !== -1;
        const result = origFetch.apply(this, arguments);
        if (isAdd) {
          result.then(function (r) {
            if (!r || (r.status >= 200 && r.status < 300)) stampCart(false);
          }).catch(function () {});
        }
        return result;
      };
    }

    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__clarityIsAddToCart = (typeof url === 'string' && url.indexOf('/cart/add') !== -1);
      return origOpen.apply(this, arguments);
    };
    const origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (this.__clarityIsAddToCart) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) stampCart(false);
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  // --- INTERCEPTORS: payment/checkout intent clicks ---
  function installPaymentIntentHook() {
    const selectorList = PAYMENT_SELECTORS.join(',');
    document.addEventListener('click', function (e) {
      try {
        const t = e.target;
        if (!t || !t.closest) return;
        if (t.closest(selectorList)) {
          // Page is likely about to navigate to an external checkout.
          stampCart(true); // keepalive
        }
      } catch (err) { /* silent */ }
    }, true); // capture phase — run before the framework's own handlers
  }

  // --- INTERCEPTORS: cart drawer / wallet UIs ---
  function installWalletShownHook() {
    // Shopify dispatches this on its dynamic checkout iframe ready
    document.addEventListener('shopify:payment_button:loaded', function () {
      // Don't stamp yet — the user hasn't clicked. But arm a one-shot on the
      // wallet iframe area in case the click target is the iframe itself.
    }, false);

    // Some themes dispatch a 'cart:added' or similar after AJAX add — be lenient
    ['cart:added', 'cart:refresh', 'cart-drawer:open'].forEach(function (evt) {
      document.addEventListener(evt, function () { stampCart(false); }, false);
    });
  }

  // --- STAMP on cart/checkout pages (real intent, cart already exists) ---
  function stampIfOnCartOrCheckoutPage() {
    const p = window.location.pathname;
    if (p === '/cart' || p.indexOf('/cart/') === 0 || p.indexOf('/checkout') === 0 || p.indexOf('/checkouts') === 0) {
      stampCart(false);
    }
  }

  // --- MAIN ---
  function init() {
    captureAttribution();
    installAddToCartHook();
    installPaymentIntentHook();
    installWalletShownHook();
    stampIfOnCartOrCheckoutPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
